import { useCallback, useEffect, useMemo, useState } from 'react';
import { Link } from 'react-router-dom';
import {
  timeApi, disbursementApi, invoiceApi, billOfCostsApi,
  type TimeEntry, type Disbursement,
} from '../../api';
import { downloadAuthorisedBlob } from '../../lib/download';

type Props = { matterId: number | string };

const asBool = (v: unknown) => v === true || v === 'true';

export default function BillingPanel({ matterId }: Props) {
  const [time, setTime] = useState<TimeEntry[]>([]);
  const [disb, setDisb] = useState<Disbursement[]>([]);
  const [loading, setLoading] = useState(true);
  const [creating, setCreating] = useState(false);
  const [pickingForProforma, setPickingForProforma] = useState(false);
  const [justCreated, setJustCreated] = useState<{ id: number; number: string } | null>(null);
  const [addingDisb, setAddingDisb] = useState(false);
  const [exporting, setExporting] = useState<'70' | '67A' | null>(null);
  const [exportError, setExportError] = useState<string | null>(null);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [t, d] = await Promise.all([
        timeApi.list({ matter_id: matterId }),
        disbursementApi.list({ matter_id: matterId }),
      ]);
      setTime(t.time_entries || []);
      setDisb(d.disbursements || []);
    } finally { setLoading(false); }
  }, [matterId]);

  useEffect(() => { load(); }, [load]);

  const totals = useMemo(() => {
    let hours = 0, unbilledHours = 0, unbilledFeeZar = 0;
    for (const e of time) {
      const h = Number(e.duration_hours) || 0;
      hours += h;
      if (asBool(e.billable) && e.billing_status === 'unbilled') {
        unbilledHours += h;
        // We don't have live rate resolution here — approximate with stored rate if available
      }
    }
    let unbilledDisbZar = 0;
    for (const d of disb) {
      if (d.billing_status === 'unbilled') {
        unbilledDisbZar += (Number(d.amount_zar) || 0) + (Number(d.vat_zar) || 0);
      }
    }
    return { hours, unbilledHours, unbilledFeeZar, unbilledDisbZar };
  }, [time, disb]);

  const exportBillOfCosts = async (scale: '70' | '67A') => {
    setExporting(scale); setExportError(null);
    try {
      await downloadAuthorisedBlob(billOfCostsApi.pdfUrl(matterId, scale));
    } catch (e) {
      setExportError((e as Error).message);
    } finally {
      setExporting(null);
    }
  };

  const openProformaPicker = () => {
    if (totals.unbilledHours === 0 && totals.unbilledDisbZar === 0) {
      alert('Nothing to bill on this matter.');
      return;
    }
    setPickingForProforma(true);
  };

  const submitProforma = async (timeIds: string[], disbIds: string[]) => {
    setCreating(true);
    try {
      // matter_ids must be digit strings — Number() rounds Catalyst's
      // 17-19 digit ROWIDs past JS safe-integer.
      const r = await invoiceApi.proforma({
        matter_ids: [String(matterId)],
        time_entry_ids: timeIds,
        disbursement_ids: disbIds,
      });
      setJustCreated({ id: Number(r.id), number: r.number });
      setPickingForProforma(false);
      await load();
    } catch (e) {
      alert((e as Error).message);
    } finally {
      setCreating(false);
    }
  };

  return (
    <div className="stack">
      <div className="row">
        <h3 className="grow" style={{ margin: 0 }}>Billing</h3>
        <button onClick={() => exportBillOfCosts('70')} disabled={exporting !== null} title="Export the bill of costs against Rule 70 (High Court)">
          {exporting === '70' ? '…' : 'Bill of costs · R70'}
        </button>
        <button onClick={() => exportBillOfCosts('67A')} disabled={exporting !== null} title="Export the bill of costs against Rule 67A (Magistrates' Court)">
          {exporting === '67A' ? '…' : 'Bill of costs · R67A'}
        </button>
        <button onClick={() => setAddingDisb(true)}>+ Disbursement</button>
        <button
          className="primary"
          onClick={openProformaPicker}
          disabled={creating || (totals.unbilledHours === 0 && totals.unbilledDisbZar === 0)}
        >
          {creating ? 'Creating…' : 'Create proforma'}
        </button>
      </div>

      {exportError && (
        <div style={{ color: 'var(--color-danger)', fontSize: 13 }}>Export failed: {exportError}</div>
      )}

      {justCreated && (
        <div className="card" style={{ borderColor: 'var(--color-accent)', background: 'rgba(201,163,74,0.08)' }}>
          <div className="row">
            <div className="grow">
              <b>Proforma {justCreated.number} created.</b>{' '}
              <Link to={`/invoices`}>View invoices</Link>
            </div>
            <button onClick={() => setJustCreated(null)}>Dismiss</button>
          </div>
        </div>
      )}

      <div className="row" style={{ display: 'grid', gridTemplateColumns: 'repeat(4, 1fr)', gap: 14 }}>
        <Metric label="Total hours"     value={totals.hours.toFixed(2)} suffix="h" />
        <Metric label="Unbilled hours"  value={totals.unbilledHours.toFixed(2)} suffix="h" accent />
        <Metric label="Unbilled disbursements" value={formatZar(totals.unbilledDisbZar)} />
        <Metric label="Open disbursements"    value={String(disb.filter((d) => d.billing_status === 'unbilled').length)} />
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={sectionHeader}>
          <h4 style={{ margin: 0 }}>Unbilled time</h4>
          <span className="muted" style={{ fontSize: 12 }}>{time.filter((t) => t.billing_status === 'unbilled' && asBool(t.billable)).length} entries</span>
        </div>
        {loading ? (
          <div style={{ padding: 18 }} className="muted">Loading…</div>
        ) : (
          <UnbilledTimeTable rows={time.filter((t) => t.billing_status === 'unbilled' && asBool(t.billable))} />
        )}
      </div>

      <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
        <div style={sectionHeader}>
          <h4 style={{ margin: 0 }}>Disbursements</h4>
          <span className="muted" style={{ fontSize: 12 }}>{disb.length} total</span>
        </div>
        {loading ? (
          <div style={{ padding: 18 }} className="muted">Loading…</div>
        ) : disb.length === 0 ? (
          <div style={{ padding: 18 }} className="muted">No disbursements recorded yet.</div>
        ) : (
          <DisbursementTable rows={disb} onChange={load} />
        )}
      </div>

      {addingDisb && (
        <AddDisbursementModal
          matterId={matterId}
          onClose={() => setAddingDisb(false)}
          onSaved={() => { setAddingDisb(false); load(); }}
        />
      )}

      {pickingForProforma && (
        <ProformaPickerModal
          time={time.filter((t) => t.billing_status === 'unbilled' && asBool(t.billable))}
          disbursements={disb.filter((d) => d.billing_status === 'unbilled')}
          submitting={creating}
          onCancel={() => setPickingForProforma(false)}
          onConfirm={submitProforma}
        />
      )}
    </div>
  );
}

// Pick-rows step for creating a proforma. Defaults to every unbilled row
// selected; the advocate unticks anything they want to keep for a later
// invoice. Footer shows running totals (hours + ZAR) so the impact of each
// tick is visible before submitting.
function ProformaPickerModal({
  time, disbursements, submitting, onCancel, onConfirm,
}: {
  time: TimeEntry[];
  disbursements: Disbursement[];
  submitting: boolean;
  onCancel: () => void;
  onConfirm: (timeIds: string[], disbIds: string[]) => void;
}) {
  const [timeSel, setTimeSel] = useState<Set<string>>(() => new Set(time.map((t) => String(t.ROWID))));
  const [disbSel, setDisbSel] = useState<Set<string>>(() => new Set(disbursements.map((d) => String(d.ROWID))));

  const toggle = (setFn: React.Dispatch<React.SetStateAction<Set<string>>>, id: string) =>
    setFn((cur) => {
      const next = new Set(cur);
      if (next.has(id)) next.delete(id); else next.add(id);
      return next;
    });

  const allTimeChecked = timeSel.size === time.length && time.length > 0;
  const allDisbChecked = disbSel.size === disbursements.length && disbursements.length > 0;
  const toggleAllTime = () => setTimeSel(allTimeChecked ? new Set() : new Set(time.map((t) => String(t.ROWID))));
  const toggleAllDisb = () => setDisbSel(allDisbChecked ? new Set() : new Set(disbursements.map((d) => String(d.ROWID))));

  // Selection totals — hours summed from selected time, ZAR summed from
  // selected disbursements (incl. VAT). Time fee total isn't shown because
  // rates aren't resolved client-side.
  const selectedHours = useMemo(
    () => time.filter((t) => timeSel.has(String(t.ROWID)))
              .reduce((s, t) => s + (Number(t.duration_hours) || 0), 0),
    [time, timeSel],
  );
  const selectedDisbZar = useMemo(
    () => disbursements.filter((d) => disbSel.has(String(d.ROWID)))
                       .reduce((s, d) => s + (Number(d.amount_zar) || 0) + (Number(d.vat_zar) || 0), 0),
    [disbursements, disbSel],
  );

  const nothingSelected = timeSel.size === 0 && disbSel.size === 0;

  return (
    <div style={backdrop} onClick={onCancel}>
      <div className="card" style={pickerCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 4 }}>Create proforma</h3>
        <p className="muted" style={{ fontSize: 13, marginBottom: 14 }}>
          Tick the rows to include on this proforma. Anything you leave unticked stays unbilled and can be picked up on a later invoice.
        </p>

        <div className="stack" style={{ gap: 14 }}>
          {/* Time entries */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', background: 'var(--color-surface-2)',
              borderBottom: '1px solid var(--color-border)',
            }}>
              <label className="inline" style={{ marginBottom: 0 }}>
                <input type="checkbox" checked={allTimeChecked} onChange={toggleAllTime} disabled={time.length === 0} />
                Time entries · {time.length}
              </label>
              <span className="muted" style={{ fontSize: 12 }}>
                {timeSel.size} selected · {selectedHours.toFixed(2)} h
              </span>
            </div>
            {time.length === 0 ? (
              <div style={{ padding: 14 }} className="muted">No unbilled time on this matter.</div>
            ) : (
              <div style={{ maxHeight: 260, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}></th>
                      <th>Date · time</th>
                      <th>Activity</th>
                      <th>Description</th>
                      <th style={{ width: 80, textAlign: 'right' }}>Hours</th>
                    </tr>
                  </thead>
                  <tbody>
                    {time.map((t) => {
                      const id = String(t.ROWID);
                      return (
                        <tr key={id} onClick={() => toggle(setTimeSel, id)} style={{ cursor: 'pointer' }}>
                          <td onClick={(e) => e.stopPropagation()}>
                            <input
                              type="checkbox"
                              checked={timeSel.has(id)}
                              onChange={() => toggle(setTimeSel, id)}
                            />
                          </td>
                          <td style={{ whiteSpace: 'nowrap', fontSize: 12, fontVariantNumeric: 'tabular-nums' }}>
                            <div>{t.work_date}</div>
                            {(t.start_time || t.end_time) && (
                              <div className="muted">
                                {t.start_time && t.end_time ? `${t.start_time}–${t.end_time}` : (t.start_time || '')}
                              </div>
                            )}
                          </td>
                          <td>{t.activity_code || '—'}</td>
                          <td style={{ maxWidth: 360 }}>
                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{t.description}</div>
                          </td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(t.duration_hours).toFixed(2)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>

          {/* Disbursements */}
          <div className="card" style={{ padding: 0, overflow: 'hidden' }}>
            <div style={{
              display: 'flex', alignItems: 'center', justifyContent: 'space-between',
              padding: '10px 14px', background: 'var(--color-surface-2)',
              borderBottom: '1px solid var(--color-border)',
            }}>
              <label className="inline" style={{ marginBottom: 0 }}>
                <input type="checkbox" checked={allDisbChecked} onChange={toggleAllDisb} disabled={disbursements.length === 0} />
                Disbursements · {disbursements.length}
              </label>
              <span className="muted" style={{ fontSize: 12 }}>
                {disbSel.size} selected · {formatZar(selectedDisbZar)}
              </span>
            </div>
            {disbursements.length === 0 ? (
              <div style={{ padding: 14 }} className="muted">No unbilled disbursements on this matter.</div>
            ) : (
              <div style={{ maxHeight: 220, overflowY: 'auto' }}>
                <table>
                  <thead>
                    <tr>
                      <th style={{ width: 32 }}></th>
                      <th>Date</th>
                      <th>Description</th>
                      <th>Vendor</th>
                      <th style={{ width: 110, textAlign: 'right' }}>Amount</th>
                      <th style={{ width: 80, textAlign: 'right' }}>VAT</th>
                    </tr>
                  </thead>
                  <tbody>
                    {disbursements.map((d) => {
                      const id = String(d.ROWID);
                      return (
                        <tr key={id} onClick={() => toggle(setDisbSel, id)} style={{ cursor: 'pointer' }}>
                          <td onClick={(e) => e.stopPropagation()}>
                            <input type="checkbox" checked={disbSel.has(id)} onChange={() => toggle(setDisbSel, id)} />
                          </td>
                          <td style={{ whiteSpace: 'nowrap' }}>{d.incurred_on}</td>
                          <td style={{ maxWidth: 320 }}>
                            <div style={{ whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{d.description}</div>
                          </td>
                          <td>{d.vendor || '—'}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatZar(Number(d.amount_zar) || 0)}</td>
                          <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatZar(Number(d.vat_zar) || 0)}</td>
                        </tr>
                      );
                    })}
                  </tbody>
                </table>
              </div>
            )}
          </div>
        </div>

        <div className="row" style={{ justifyContent: 'space-between', alignItems: 'center', marginTop: 16 }}>
          <div className="muted" style={{ fontSize: 12 }}>
            Selected: {timeSel.size + disbSel.size} item{timeSel.size + disbSel.size === 1 ? '' : 's'}
          </div>
          <div className="row" style={{ gap: 8 }}>
            <button onClick={onCancel} disabled={submitting} type="button">Cancel</button>
            <button
              className="primary"
              onClick={() => onConfirm([...timeSel], [...disbSel])}
              disabled={submitting || nothingSelected}
              type="button"
            >
              {submitting ? 'Creating…' : `Create proforma${nothingSelected ? '' : ` (${timeSel.size + disbSel.size})`}`}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function Metric({ label, value, suffix, accent }: { label: string; value: string; suffix?: string; accent?: boolean }) {
  return (
    <div className="card">
      <div className="muted" style={{ fontSize: 11, textTransform: 'uppercase', letterSpacing: '0.05em' }}>
        {label}
      </div>
      <div style={{ fontSize: 28, fontWeight: 700, marginTop: 6, color: accent ? 'var(--color-accent)' : 'var(--color-text)' }}>
        {value}{suffix && <span style={{ fontSize: 16, fontWeight: 500, marginLeft: 4, color: 'var(--color-text-muted)' }}>{suffix}</span>}
      </div>
    </div>
  );
}

function UnbilledTimeTable({ rows }: { rows: TimeEntry[] }) {
  if (rows.length === 0) {
    return <div style={{ padding: 18 }} className="muted">No unbilled time. Log time on the Time tab.</div>;
  }
  return (
    <table>
      <thead>
        <tr><th>Date</th><th>Activity</th><th>Description</th><th style={{ width: 80, textAlign: 'right' }}>Hours</th></tr>
      </thead>
      <tbody>
        {rows.map((e) => (
          <tr key={String(e.ROWID)}>
            <td style={{ whiteSpace: 'nowrap' }}>{e.work_date}</td>
            <td>{e.activity_code || '—'}</td>
            <td style={{ maxWidth: 480, whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis' }}>{e.description}</td>
            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{Number(e.duration_hours).toFixed(2)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function DisbursementTable({ rows, onChange }: { rows: Disbursement[]; onChange: () => void }) {
  const remove = async (id: number) => {
    if (!confirm('Delete this disbursement?')) return;
    await disbursementApi.remove(id);
    onChange();
  };
  return (
    <table>
      <thead>
        <tr>
          <th>Date</th><th>Description</th><th>Vendor</th>
          <th style={{ width: 120, textAlign: 'right' }}>Amount</th>
          <th style={{ width: 80, textAlign: 'right' }}>VAT</th>
          <th style={{ width: 120 }}>Status</th>
          <th style={{ width: 40 }}></th>
        </tr>
      </thead>
      <tbody>
        {rows.map((d) => (
          <tr key={String(d.ROWID)}>
            <td style={{ whiteSpace: 'nowrap' }}>{d.incurred_on}</td>
            <td>{d.description}</td>
            <td>{d.vendor || '—'}</td>
            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatZar(Number(d.amount_zar))}</td>
            <td style={{ textAlign: 'right', fontVariantNumeric: 'tabular-nums' }}>{formatZar(Number(d.vat_zar || 0))}</td>
            <td><span className="pill">{d.billing_status.replace('_', ' ')}</span></td>
            <td style={{ textAlign: 'right' }}>
              {d.billing_status === 'unbilled' && (
                <button className="ghost" style={{ padding: '4px 8px', color: 'var(--color-danger)' }} onClick={() => remove(Number(d.ROWID))}>×</button>
              )}
            </td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

function AddDisbursementModal({ matterId, onClose, onSaved }: { matterId: number | string; onClose: () => void; onSaved: () => void }) {
  const today = new Date().toISOString().slice(0, 10);
  const [form, setForm] = useState<NewDisbursement>({
    matter_id: matterId,
    incurred_on: today,
    description: '',
    amount_zar: 0,
    vat_zar: 0,
    vendor: '',
  });
  const [saving, setSaving] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const save = async () => {
    if (!form.description.trim()) { setError('Description is required.'); return; }
    if (!(form.amount_zar > 0))   { setError('Amount must be > 0.'); return; }
    setSaving(true);
    setError(null);
    try {
      await disbursementApi.create({
        matter_id: Number(matterId),
        incurred_on: form.incurred_on,
        description: form.description,
        amount_zar: form.amount_zar,
        vat_zar: form.vat_zar || 0,
        vendor: form.vendor || undefined,
      });
      onSaved();
    } catch (e) { setError((e as Error).message); }
    finally { setSaving(false); }
  };

  return (
    <div style={backdrop} onClick={onClose}>
      <div className="card" style={modalCard} onClick={(e) => e.stopPropagation()}>
        <h3 style={{ marginBottom: 14 }}>Add disbursement</h3>
        <div className="stack">
          <div className="row">
            <div style={{ flex: 1 }}>
              <label>Date incurred</label>
              <input type="date" value={form.incurred_on} onChange={(e) => setForm({ ...form, incurred_on: e.target.value })} />
            </div>
            <div style={{ flex: 1 }}>
              <label>Vendor</label>
              <input value={form.vendor || ''} onChange={(e) => setForm({ ...form, vendor: e.target.value })} />
            </div>
          </div>
          <div>
            <label>Description</label>
            <input autoFocus value={form.description} onChange={(e) => setForm({ ...form, description: e.target.value })} />
          </div>
          <div className="row">
            <div style={{ flex: 1 }}>
              <label>Amount (ZAR, excl. VAT)</label>
              <input type="number" step="0.01" value={form.amount_zar || ''} onChange={(e) => setForm({ ...form, amount_zar: Number(e.target.value) })} />
            </div>
            <div style={{ flex: 1 }}>
              <label>VAT (ZAR)</label>
              <input type="number" step="0.01" value={form.vat_zar || ''} onChange={(e) => setForm({ ...form, vat_zar: Number(e.target.value) })} />
            </div>
          </div>
          {error && <div style={{ color: 'var(--color-danger)' }}>{error}</div>}
          <div className="row" style={{ justifyContent: 'flex-end' }}>
            <button onClick={onClose} disabled={saving}>Cancel</button>
            <button className="primary" onClick={save} disabled={saving}>
              {saving ? 'Saving…' : 'Save disbursement'}
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

type NewDisbursement = {
  matter_id: number | string;
  incurred_on: string;
  description: string;
  amount_zar: number;
  vat_zar?: number;
  vendor?: string;
};

function formatZar(n: number) {
  return new Intl.NumberFormat('en-ZA', { style: 'currency', currency: 'ZAR', minimumFractionDigits: 2 }).format(n || 0);
}

const sectionHeader: React.CSSProperties = {
  display: 'flex', alignItems: 'center', justifyContent: 'space-between',
  padding: '14px 18px', borderBottom: '1px solid var(--color-border)', background: 'var(--color-surface-2)',
};
const backdrop: React.CSSProperties = {
  position: 'fixed', inset: 0, background: 'rgba(0,0,0,0.4)',
  display: 'flex', alignItems: 'center', justifyContent: 'center', zIndex: 100,
};
const modalCard: React.CSSProperties = { width: 560, maxWidth: '90vw' };
const pickerCard: React.CSSProperties = { width: 880, maxWidth: '94vw', maxHeight: '88vh', overflowY: 'auto' };
